import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Analytics hook for tracking user interactions on demo page
 * Tracks: page views, tab switches, button clicks, time spent, feature engagement
 */
export const useDemoAnalytics = (sessionId) => {
  const startTime = useRef(Date.now());
  const lastActivity = useRef(Date.now());
  const eventsQueue = useRef([]);
  const flushTimeout = useRef(null);

  // Flush events to backend
  const flushEvents = useCallback(async () => {
    if (eventsQueue.current.length === 0) return;
    
    const events = [...eventsQueue.current];
    eventsQueue.current = [];
    
    try {
      await axios.post(`${API}/analytics/demo/events`, {
        session_id: sessionId,
        events,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.debug('Analytics flush failed:', error);
    }
  }, [sessionId]);

  // Queue an event
  const trackEvent = useCallback((eventType, data = {}) => {
    const event = {
      type: eventType,
      data,
      timestamp: new Date().toISOString(),
      time_since_start: Math.round((Date.now() - startTime.current) / 1000)
    };
    
    eventsQueue.current.push(event);
    lastActivity.current = Date.now();
    
    // Debounce flush
    if (flushTimeout.current) clearTimeout(flushTimeout.current);
    flushTimeout.current = setTimeout(flushEvents, 3000);
  }, [flushEvents]);

  // Track page view on mount
  useEffect(() => {
    trackEvent('page_view', { page: 'demo' });
    
    // Flush on unmount
    return () => {
      if (flushTimeout.current) clearTimeout(flushTimeout.current);
      
      // Calculate total time spent
      const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
      eventsQueue.current.push({
        type: 'session_end',
        data: { time_spent_seconds: timeSpent },
        timestamp: new Date().toISOString()
      });
      
      // Use sendBeacon for reliable delivery on page unload
      const data = JSON.stringify({
        session_id: sessionId,
        events: eventsQueue.current,
        timestamp: new Date().toISOString()
      });
      navigator.sendBeacon?.(`${API}/analytics/demo/events`, data);
    };
  }, [sessionId, trackEvent]);

  // Track specific events
  const trackTabView = useCallback((tabName) => {
    trackEvent('tab_view', { tab: tabName });
  }, [trackEvent]);

  const trackButtonClick = useCallback((buttonName, location) => {
    trackEvent('button_click', { button: buttonName, location });
  }, [trackEvent]);

  const trackFeatureEngagement = useCallback((feature, action) => {
    trackEvent('feature_engagement', { feature, action });
  }, [trackEvent]);

  const trackTooltipView = useCallback((tooltipId) => {
    trackEvent('tooltip_view', { tooltip_id: tooltipId });
  }, [trackEvent]);

  const trackSearch = useCallback((query) => {
    trackEvent('search', { query });
  }, [trackEvent]);

  const trackCTAClick = useCallback((ctaType, location) => {
    trackEvent('cta_click', { cta_type: ctaType, location });
  }, [trackEvent]);

  return {
    trackEvent,
    trackTabView,
    trackButtonClick,
    trackFeatureEngagement,
    trackTooltipView,
    trackSearch,
    trackCTAClick
  };
};

// Generate a unique session ID
export const generateSessionId = () => {
  return `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
